<!-- RELEASE REGION SELECTION MODAL -->
<template>
  <b-modal
    id="genericReleaseModal"
    content-class="generic-release-modal"
    size="lg"
    hide-footer
    hide-header-close
    centered
    @show="selected = false"
    @hide="handleClose"
  >
    <!-- Custom Header -->
    <template #modal-header="{ close }">
      <div class="grm-header d-flex align-items-center w-100">
        <div class="grm-header-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#4db8ff" stroke-width="1.5"/>
            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" stroke="#4db8ff" stroke-width="1.5"/>
            <path d="M4.93 4.93a16 16 0 0 1 14.14 0M4.93 19.07a16 16 0 0 0 14.14 0" stroke="#4db8ff" stroke-width="1.5"/>
          </svg>
        </div>
        <div class="grm-header-text">
          <h5 class="grm-title mb-0">Select a Release</h5>
          <p class="grm-subtitle mb-0">Choose a dataset to explore on the map</p>
        </div>
        <div class="grm-header-badge ml-auto">
          {{ releases.length }} release{{ releases.length !== 1 ? 's' : '' }} available
        </div>
        <fa :icon="['fas', 'times']" @click="close()" class="grm-header-close"/>
      </div>
    </template>

    <!-- Release Cards Grid -->
    <b-container fluid class="grm-body">
      <b-row v-if="releases.length === 0" class="justify-content-center">
        <b-col cols="12" class="text-center grm-empty">
          <div class="grm-empty-icon">◎</div>
          <p>No releases available</p>
        </b-col>
      </b-row>

      <b-row class="grm-grid">
        <b-col
          v-for="release in releases"
          :key="release.id"
          cols="12"
          sm="6"
          class="grm-card-col mb-3"
        >
          <div class="grm-card" @click="datasetSelected(release)">
            <!-- Map Preview -->
            <div class="grm-map-preview">
              <div class="grm-map-glow"></div>
              <img
                v-if="getRegionImage(release.extent_description)"
                :src="getRegionImage(release.extent_description)"
                :alt="release.extent_description"
                class="grm-map-img"
              />
              <div v-else class="grm-map-placeholder">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="grm-globe-icon">
                  <circle cx="12" cy="12" r="10" stroke="#4db8ff" stroke-width="1.5"/>
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" stroke="#4db8ff" stroke-width="1.5"/>
                </svg>
              </div>
            </div>

            <!-- Card Info -->
            <div class="grm-card-info">
              <div class="grm-region-name">{{ release.extent_description }}</div>
              <div class="grm-release-label" v-if="release.release_label">{{ release.release_label }}</div>
              <div class="grm-release-date" v-if="!wclFlag">
                <span class="grm-date-dot"></span>
                {{ formatDate(release.release_date) }}
              </div>
            </div>

            <!-- Select CTA -->
            <div class="grm-card-action">
              <span class="grm-action-label">Select</span>
              <svg class="grm-action-arrow" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>

            <!-- Hover scan line effect -->
            <div class="grm-card-shine"></div>
          </div>
        </b-col>
      </b-row>
    </b-container>

    <!-- Footer -->
    <template #modal-footer>
      <button
        type="button"
        class="grm-close-btn"
        @click="$bvModal.hide('genericReleaseModal')"
      >
        Close
      </button>
    </template>
  </b-modal>
</template>

<style>
/* ── Modal shell ── */
.generic-release-modal .modal-content {
  background: transparent;
  border: none;
}

.generic-release-modal .modal-header {
  background: #063c6d;
  color: white;
  border-bottom: 1px solid rgba(77, 184, 255, 0.2);
  padding: 1.1rem 1.4rem;
}

.generic-release-modal .modal-body {
  border-radius: 0 0 12px 12px;
  padding: 0;
}

/* ── Header ── */
.grm-header { gap: 0.875rem; }

.grm-header-icon {
  width: 2.5rem;
  height: 2.5rem;
  background: rgba(77, 184, 255, 0.12);
  border: 1px solid rgba(77, 184, 255, 0.3);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.grm-header-icon svg { width: 1.375rem; height: 1.375rem; }

.grm-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #e8f4ff;
  letter-spacing: 0.02em;
}

.grm-subtitle {
  font-size: 0.72rem;
  letter-spacing: 0.01em;
}

.grm-header-badge {
  background: rgba(77, 184, 255, 0.12);
  border: 1px solid rgba(77, 184, 255, 0.25);
  color: #4db8ff;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 20px;
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.grm-header-close {
  color: whitesmoke;
  font-size: 1rem;
  cursor: pointer;
  margin-left: 0.875rem;
  transition: color 0.15s ease;
}

.grm-header-close:hover {
  opacity: 0.75;
}

/* ── Footer ── */
.generic-release-modal .modal-footer {
  background: #ffffff;
  border-top: 1px solid rgba(6, 60, 109, 0.1);
  border-radius: 0 0 12px 12px;
  padding: 0.75rem 1.2rem;
  justify-content: flex-end;
}

/* ── Close button ── */
.grm-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 6rem;
  padding: 0.5rem 1.6rem;
  border: none;
  border-radius: 22px;
  background: #063c6d;
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
}

.grm-close-btn:hover {
  filter: brightness(1.12);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(6, 60, 109, 0.3);
}

.grm-close-btn:active {
  transform: translateY(0);
}

/* ── Body / grid ── */
.grm-body {
  padding: 1.2rem 1.2rem 0.8rem;
}

.grm-grid { margin: 0 -0.5rem; }
.grm-card-col { padding: 0 0.5rem; }

/* ── Card ── */
.grm-card {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(77, 184, 255, 0.15);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.grm-card:hover {
  transform: translateY(-2px);
  border-color: rgba(77, 184, 255, 0.55);
  box-shadow: 0 8px 32px rgba(4, 150, 233, 0.2), 0 0 0 1px rgba(77, 184, 255, 0.2) inset;
}

.grm-card:hover .grm-card-shine {
  left: 120%;
}

.grm-card:hover .grm-map-glow {
  opacity: 1;
}

.grm-card:hover .grm-action-label {
  color: #4db8ff;
}

.grm-card:hover .grm-action-arrow {
  color: #4db8ff;
  transform: translateX(3px);
}

/* Shine sweep */
.grm-card-shine {
  position: absolute;
  top: 0;
  left: -60%;
  width: 40%;
  height: 100%;
  background: linear-gradient(105deg, transparent, rgba(255,255,255,0.06), transparent);
  transform: skewX(-15deg);
  transition: left 0.5s ease;
  pointer-events: none;
}

/* ── Icon preview panel ── */
.grm-map-preview {
  position: relative;
  width: 6.875rem;
  min-width: 6.875rem;
  height: 5.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #041e3a 0%, #063c6d 60%, #0a5499 100%);
  border-right: 1px solid rgba(77, 184, 255, 0.1);
  overflow: hidden;
  flex-shrink: 0;
}

.grm-map-glow {
  position: absolute;
  width: 4.375rem;
  height: 4.375rem;
  background: radial-gradient(circle, rgba(4, 150, 233, 0.25) 0%, transparent 70%);
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.grm-map-img {
  width: 4.5rem;
  height: auto;
  max-height: 4.25rem;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(4, 150, 233, 0.4)) brightness(1.05);
  position: relative;
  z-index: 1;
  transition: filter 0.2s ease, transform 0.2s ease;
}

.grm-card:hover .grm-map-img {
  filter: drop-shadow(0 4px 14px rgba(4, 150, 233, 0.65)) brightness(1.15);
  transform: scale(1.05);
}

.grm-map-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
}

.grm-globe-icon { width: 3rem; height: 3rem; }

/* ── Card info ── */
.grm-card-info {
  flex: 1;
  padding: 0 0.875rem;
  min-width: 0;
}

.grm-region-name {
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.1875rem;
}

.grm-release-label {
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.1875rem;
}

.grm-release-date {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.72rem;
  font-weight: 500;
}

.grm-date-dot {
  width: 0.3125rem;
  height: 0.3125rem;
  background: #4db8ff;
  border-radius: 50%;
  flex-shrink: 0;
  opacity: 0.7;
}

/* ── Action area ── */
.grm-card-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 0 0.875rem 0 0.375rem;
  flex-shrink: 0;
}

.grm-action-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: color 0.2s ease;
}

.grm-action-arrow {
  width: 1.125rem;
  height: 1.125rem;
  transition: color 0.2s ease, transform 0.2s ease;
}

/* ── Empty state ── */
.grm-empty { padding: 2.5rem 0; color: #063c6d; }
.grm-empty-icon { font-size: 2.5rem; margin-bottom: 0.5rem; opacity: 0.4; }
</style>

<script>
import axios from 'axios'
import australiaImg from '../../../assets/extents/Australia.svg'
import europeImg from '../../../assets/extents/Europe.svg'
import japanImg from '../../../assets/extents/Japan.svg'
import middleEastImg from '../../../assets/extents/MiddleEast.svg'
import newZealandImg from '../../../assets/extents/NewZealand.svg'
import northAmericaImg from '../../../assets/extents/NorthAmerica.svg'
import southKoreaImg from '../../../assets/extents/SouthKorea.svg'

const REGION_MAP = [
  { keys: ['australia'],                                                img: australiaImg },
  { keys: ['europe'],                                                   img: europeImg },
  { keys: ['japan'],                                                    img: japanImg },
  { keys: ['middle east', 'middleeast'],                               img: middleEastImg },
  { keys: ['new zealand', 'newzealand'],                               img: newZealandImg },
  { keys: ['north america', 'northamerica', 'usa', 'united states'],  img: northAmericaImg },
  { keys: ['south korea', 'southkorea', 'korea'],                     img: southKoreaImg },
]

export default {
    name: 'Generic_ReleaseModal',
    props: ['project_id', 'wclFlag'],
    data() {
        return {
          releases: [],
          // Tracks whether a release was actually picked before the modal
          // closes — the parent also hides this modal programmatically right
          // after a valid selection, so @hide alone can't tell "user closed
          // it" apart from that.
          selected: false
        }
    },
    methods: {
      datasetSelected(release) {
        this.selected = true
        this.$emit('datasetSelected', release)
      },
      handleClose() {
        if (!this.selected) {
          this.$emit('returnHome')
        }
      },
      getRegionImage(extentDescription) {
        if (!extentDescription) return null
        const lower = extentDescription.toLowerCase()
        for (const entry of REGION_MAP) {
          if (entry.keys.some(k => lower.includes(k))) return entry.img
        }
        return null
      },
      formatDate(dateStr) {
        if (!dateStr) return '—'
        return new Date(dateStr).toLocaleDateString('en-US', {
          year: 'numeric', month: 'short', day: 'numeric'
        })
      },
      getReleases() {
        if (sessionStorage.token) {
          axios.get(process.env.API_LOCATION + '/api/release/project/' + this.project_id, {
            method: 'GET',
            headers: {
              'x-auth-token': sessionStorage.token
            }
          })
          .then(response => {
            const content = response.data
            this.releases = content
          })
          .catch(function (error) {
            console.log(error)
          })
        } else {
            console.log('No User Token')
        }
      },
    },
    watch: {
      project_id: function(newValue) {
        if (newValue) {
          this.getReleases()
        }
      }
    }
}
</script>
